import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Box, Card, CardMedia, Paper, Typography, useTheme } from '@mui/material';

import { useGetFeaturedWebcamCaptures, WebcamData, WebcamDataAiViolation } from '@/api/generated';
import Loader from '@/components/Loader/Loader';
import { useFormattedDatetime } from '@/hooks/useFormattedDatetime';

const AI_VIOLATION_I18N_KEYS: Record<WebcamDataAiViolation, string> = {
  NO_FACE_DETECTED: 'noFaceDetected',
  MORE_THAN_ONE_FACE_DETECTED: 'moreThanOneFaceDetected',
  FACE_DIFFERENT_FROM_PROFILE: 'faceDifferentFromProfile'
};

const CARD_WIDTH = '237px'; // we find this size to work best.

/*
Image size rationale:
it was decided to use a 16:9 aspect ratio for the img.

if the image is 4:3, it will be scaled to fit (object-fit: contain) inside 16:9 
in this case you get empty space on the sides, which allows more room for the violation label and the timestamp.
*/

export const Gallery = ({ testSessionId }: { testSessionId: string }) => {
  const { data: featuredWebcamCapturesData, isLoading } = useGetFeaturedWebcamCaptures(testSessionId, {
    query: { enabled: !!testSessionId }
  });

  const featuredWebcamCaptures = featuredWebcamCapturesData?.featuredCaptures || [];
  const count = featuredWebcamCaptures.length;
  const cols = (n: number) => (n === 1 ? CARD_WIDTH : `repeat(${n}, ${CARD_WIDTH})`);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Paper elevation={0} sx={{ padding: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: cols(Math.min(1, count)),
            sm: cols(Math.min(2, count)),
            md: cols(Math.min(3, count)),
            lg: cols(Math.min(4, count))
          },
          gap: 2,
          justifyContent: { lg: count >= 4 ? 'space-between' : 'flex-start' }
        }}
      >
        {featuredWebcamCaptures.map((img, index) => (
          <ImageCard key={index} imageData={img} />
        ))}
      </Box>
    </Paper>
  );
};

export const ImageCard = ({ imageData }: { imageData: WebcamData }) => {
  const theme = useTheme();
  const { t } = useTranslation('tests');
  const { formatDatetime } = useFormattedDatetime();

  const hasViolation = Boolean(imageData.aiViolation);
  const violationLabel = imageData.aiViolation
    ? t(`aiViolations.${AI_VIOLATION_I18N_KEYS[imageData.aiViolation]}`)
    : t('aiViolations.noViolation');

  return (
    <Card
      sx={{
        border: hasViolation ? `2px solid ${theme.palette.error.main}` : 'none',
        position: 'relative',
        width: CARD_WIDTH,
        minWidth: 0
      }}
    >
      <CardMedia
        component="img"
        alt={violationLabel}
        src={imageData.imageUrl}
        sx={{ width: '100%', display: 'block', objectFit: 'contain', aspectRatio: '16/9' }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          p: theme.spacing(0.5),
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}
      >
        {hasViolation && (
          <Typography variant="body3" color="error">
            {violationLabel}
          </Typography>
        )}
        <Typography variant="body3" sx={{ marginInlineStart: 'auto' }}>
          {formatDatetime(imageData.timestamp)}
        </Typography>
      </Box>
    </Card>
  );
};
